import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteSearch } from '@/lib/queries';
import type { SearchParams as SP } from '@shared/schemas';

// Parametri che indicano una ricerca/navigazione attiva (page/sort non contano).
const ACTIVE_KEYS = ['titolo', 'testo', 'quaderni', 'q', 'anno', 'mese', 'fonte', 'dataFrom', 'dataTo'] as const;

/**
 * Logica condivisa fra la home (`/cerca`) e la gestione articoli admin: legge i
 * parametri di ricerca dalla URL, esegue la query e offre ordinamento + ritorno
 * all'indice. La UI dei risultati vive in `<ArticleResults>`.
 */
export function useArticleSearch() {
  const [searchParams, setSearchParams] = useSearchParams();

  const isActive = ACTIVE_KEYS.some((k) => (searchParams.get(k) ?? '') !== '');

  const params = useMemo<Partial<SP>>(() => {
    const obj: Record<string, string> = {};
    for (const [k, v] of searchParams.entries()) {
      if (v) obj[k] = v;
    }
    return {
      q: obj.q,
      titolo: obj.titolo,
      testo: obj.testo,
      quaderni: obj.quaderni,
      anno: obj.anno ? Number(obj.anno) : undefined,
      mese: obj.mese ? Number(obj.mese) : undefined,
      fonte: obj.fonte,
      dataFrom: obj.dataFrom,
      dataTo: obj.dataTo,
      page: obj.page ? Number(obj.page) : 1,
      sort: (obj.sort as SP['sort']) ?? 'date_asc',
    };
  }, [searchParams]);

  const search = useInfiniteSearch(params, isActive);
  const sort = searchParams.get('sort') ?? 'date_asc';

  function setSort(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === 'date_asc') next.delete('sort');
    else next.set('sort', value);
    next.delete('page');
    setSearchParams(next);
  }

  function backToIndex() {
    setSearchParams(new URLSearchParams());
  }

  return { isActive, params, search, sort, setSort, backToIndex };
}

export type ArticleSearch = ReturnType<typeof useArticleSearch>;
